/**
 * Location Search Query Builder
 *
 * Builds ElasticSearch queries for location autocomplete with:
 * - function_score with field_value_factor for adCount boosting
 * - Multiple match_phrase and multi_match clauses with different boosts
 * - Filters for hierarchy and level (Lamudi format: hierarchy.externalID, level>2)
 * - Highlighting for name field
 */

/**
 * Build filter options for sub-location search
 * This replaces Algolia.getSubLocationsQuery - no Algolia dependency
 *
 * @param {string|number} locationId - Parent location externalID
 * @param {Array<string|number>} exclude - External IDs to exclude
 * @returns {Object} - { filters: string }
 */
export const getSubLocationsFilter = (locationId, exclude = []) => {
  let filters = `hierarchy.externalID:${locationId} AND level>2`
  if (exclude.length > 0) {
    filters += " AND NOT " + exclude.map((item) => `externalID:${item}`).join(" AND NOT ")
  }
  return { filters }
}

/**
 * Parse a simple condition like "attribute:value" or "level>2"
 * @param {string} condition - Simple filter condition
 * @returns {Object|null} - ES query clause or null
 */
const parseSimpleCondition = (condition) => {
  const trimmed = condition.trim()

  // Handle range: level>2 or level<5 or level>=2 or level<=5
  const rangeMatch = trimmed.match(/(\w+(?:\.\w+)*)\s*(>=|<=|>|<)\s*(\d+)/)
  if (rangeMatch) {
    const [, attr, op, val] = rangeMatch
    const rangeClause = {}

    if (op === ">") {
      rangeClause.gt = Number(val)
    } else if (op === ">=") {
      rangeClause.gte = Number(val)
    } else if (op === "<") {
      rangeClause.lt = Number(val)
    } else if (op === "<=") {
      rangeClause.lte = Number(val)
    }

    return { range: { [attr]: rangeClause } }
  }

  // Handle exact match: attribute:value (e.g. hierarchy.externalID:41)
  if (trimmed.includes(":") && !trimmed.includes(">") && !trimmed.includes("<")) {
    const colonIndex = trimmed.indexOf(":")
    const attr = trimmed.slice(0, colonIndex).trim()
    const value = trimmed.slice(colonIndex + 1).trim()

    if (attr && value) {
      // For externalID fields, keep as string since ES indexes them as strings
      const isExternalIdField = attr.toLowerCase().includes('externalid')
      const finalValue = isExternalIdField 
        ? value 
        : (isNaN(value) ? value : Number(value))

      return { term: { [attr]: finalValue } }
    }
  }

  return null
}

/**
 * Parse Algolia-style filter string to ES filters
 * Keeps Lamudi's filter format:
 * - hierarchy.externalID (not converted to slug)
 * - level>2 (gt:2, not lte:9)
 * 
 * Supports:
 * - "hierarchy.externalID:41 AND level>2 AND NOT externalID:456"
 * - "externalID:11734 OR externalID:681" (for initChildLocs)
 * - "(externalID:11734 OR externalID:681) AND level>2"
 *
 * @param {string} filterString - Algolia-style filter string
 * @returns {Object} - { must: [], must_not: [] }
 */
const parseAlgoliaFiltersToES = (filterString) => {
  const must = []
  const mustNot = []

  if (!filterString) {
    return { must, must_not: mustNot }
  }

  // Check if entire filter is just OR conditions (no AND)
  // e.g., "externalID:11734 OR externalID:681"
  if (!filterString.includes(" AND ") && filterString.includes(" OR ")) {
    const orParts = filterString.split(" OR ")
    const orClauses = []

    orParts.forEach((orPart) => {
      const clause = parseSimpleCondition(orPart.trim())
      if (clause) {
        orClauses.push(clause)
      }
    })

    if (orClauses.length > 0) {
      must.push({
        bool: {
          should: orClauses,
          minimum_should_match: 1,
        },
      })
    }

    return { must, must_not: mustNot }
  }

  // Split by AND, handling "AND NOT" specially
  const parts = filterString.split(/\s+AND\s+/)

  parts.forEach((part) => {
    const trimmed = part.trim()

    // Handle NOT conditions
    if (trimmed.startsWith("NOT ")) {
      const condition = trimmed.replace("NOT ", "").trim()
      const clause = parseSimpleCondition(condition)
      if (clause) {
        mustNot.push(clause.term ? clause : clause)
      }
      return
    }

    // Handle OR conditions in parentheses: (externalID:11734 OR externalID:681)
    if (trimmed.startsWith("(") && trimmed.endsWith(")") && trimmed.includes(" OR ")) {
      const inner = trimmed.slice(1, -1) // Remove parentheses
      const orParts = inner.split(" OR ")
      const orClauses = []

      orParts.forEach((orPart) => {
        const clause = parseSimpleCondition(orPart.trim())
        if (clause) {
          orClauses.push(clause)
        }
      })

      if (orClauses.length > 0) {
        must.push({
          bool: {
            should: orClauses,
            minimum_should_match: 1,
          },
        })
      }
      return
    }

    // Handle simple conditions
    const clause = parseSimpleCondition(trimmed)
    if (clause) {
      must.push(clause)
    }
  })

  return { must, must_not: mustNot }
}

/**
 * Build the location search query for ElasticSearch
 *
 * @param {string} query - Search query text
 * @param {Object} options - Search options
 * @param {string} [options.filters] - Algolia-style filter string (e.g. "hierarchy.externalID:41 AND level>2")
 * @param {number} [options.hitsPerPage=20] - Number of results
 * @returns {Object} - ElasticSearch request body
 */
export const buildLocationSearchQuery = (query, options = {}) => {
  const { filters, hitsPerPage = 20 } = options

  // Parse Algolia-style filters if provided
  const parsedFilters = parseAlgoliaFiltersToES(filters)
  const must = parsedFilters.must
  const mustNot = parsedFilters.must_not

  // Build the should clauses for text matching (matching Mustang exactly)
  const should = []

  if (query && query.trim()) {
    const trimmedQuery = query.trim()

    // 1. Exact phrase match on name (highest boost)
    should.push({
      match_phrase: {
        name: {
          query: trimmedQuery,
          boost: 10,
        },
      },
    })

    // 2. Phrase match on hierarchyPath (high boost)
    should.push({
      match_phrase: {
        hierarchyPath: {
          query: trimmedQuery,
          boost: 8,
        },
      },
    })

    // 3. Another hierarchyPath phrase match (medium boost)
    should.push({
      match_phrase: {
        hierarchyPath: {
          query: trimmedQuery,
          boost: 5,
        },
      },
    })

    // 4. Multi-match with bool_prefix for autocomplete
    should.push({
      multi_match: {
        query: trimmedQuery,
        type: "bool_prefix",
        fields: ["name^4", "name._2gram", "name._3gram", "aliases^3", "query^2"],
        operator: "and",
      },
    })

    // 5. Fuzzy multi-match for typo tolerance
    should.push({
      multi_match: {
        query: trimmedQuery,
        type: "most_fields",
        fuzziness: "AUTO",
        fields: ["name", "aliases", "hierarchyPath"],
        operator: "and",
        boost: 1,
      },
    })
  }

  // Build the bool query (order matches fe-search-redux exactly: should, minimum_should_match, must, must_not)
  const boolQuery = {}

  if (should.length > 0) {
    boolQuery.should = should
  }

  if (should.length > 0) {
    boolQuery.minimum_should_match = 1
  }

  if (must.length > 0) {
    boolQuery.must = must
  }

  if (mustNot.length > 0) {
    boolQuery.must_not = mustNot
  }

  // Build the full request with function_score
  const request = {
    from: 0,
    size: hitsPerPage,
    track_total_hits: 10000,
    query: {
      function_score: {
        query: {
          bool: boolQuery,
        },
        functions: [
          {
            field_value_factor: {
              field: "adCount",
              factor: 1,
              modifier: "log1p",
              missing: 0,
            },
            weight: 2,
          },
        ],
        score_mode: "sum",
      },
    },
    highlight: {
      fields: {
        name: {},
      },
    },
  }

  return request
}

export default buildLocationSearchQuery
